import { Button } from "antd";
import { ReactElement, useEffect, useState } from "react";

import "./edit-contact-persons.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import instance from "../../store/axios";
import { LOADING } from "../../store/actions";
import toast from "react-hot-toast";
import CollapseComponent from "../../components/collapse.component";
import CustomModal from "../../components/custom-modal/custom-modal";
import DeleteModal from "../../components/delete-modal/delete-modal";
import { PlusSquareOutlined } from "@ant-design/icons";
import ContactPersonForm from "./contact-person-form";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";

const EditContactPersons: React.FC = (): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const [accountDetails, setAccountDetails] = useState<any>();
  const [standingData, setStandingData] = useState<any>();
  const [openDeleteModal, setOpenDeleteModal] = useState<any>(false);
  const [selectedContactPerson, setSelectedContactPerson] = useState<any>();
  const [contactPersonsArray, setContactPersonsArray] = useState<any>([]);
  const [countriesList, setCountriesList] = useState<any>([]);

  useEffect(() => {
    async function prepareData() {
      dispatch({ type: LOADING, payload: true });

      await getCountriesList();
      await getStandindData();
      await getKycApi();
      dispatch({ type: LOADING, payload: false });
    }

    prepareData();
  }, []);

  const getKycApi = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      if (response.data.data?.contactPersons?.length) {
        setContactPersonsArray(response.data.data?.contactPersons);
      } else {
        setContactPersonsArray([{}]);
      }
      setAccountDetails(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit-contact-persons`
      );
    }
  };

  const getStandindData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit-contact-persons`
      );
    }
  };

  const getCountriesList = async () => {
    try {
      const response: AxiosResponse<{
        data: any[];
        message: string;
      }> = await instance.get(`/${process.env.REACT_APP_ENDPOINT_VERSION}/option/countries?partial=${1}`);
      setCountriesList(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit-contact-persons`
      );
      return undefined;
    }
  };

  const onSubmit = async (values: any): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    values.kycId = accountDetails.accountInfo[0]?.id;
    values.branch = "KW";

    try {
      if (!values?.id) {
        delete values?.id;
        await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/contact-persons`, values);
      } else {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/contact-persons/${values?.id}`,
          values
        );
      }

      toast.success("Contact persons data updated successfully");
      navigate(
        `/home/business-account/${accountId}/${activeKyc}/details?tab=1${isApproveCase ? "&approve=1" : ""}`
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit-contact-persons${isApproveCase ? "?approve=1" : ""}`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  const deleteContactPerson = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/contact-persons/${selectedContactPerson?.id}/delete`
      );
      toast.success("Contact Persons deleted successfully");
      setOpenDeleteModal(false);
      navigate(
        `/home/business-account/${accountId}/${activeKyc}/details?tab=1`
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit-contact-persons`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  return (
    <div className="edit-contact-persons-container flex flex-col">
      <BreadcrumbComponent />
      <h3 className=" font-semibold text-xl mb-5">Contact persons details</h3>

      {accountDetails &&
        contactPersonsArray?.map((person: any, index: number) => (
          <CollapseComponent
            key={index}
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">Person #{index + 1}</h3>
              </div>
            }
          >
            <ContactPersonForm
              person={person}
              index={index}
              standingData={standingData}
              countriesList={countriesList}
              setSelectedContactPerson={setSelectedContactPerson}
              setOpenDeleteModal={setOpenDeleteModal}
              onSubmit={onSubmit}
            />
          </CollapseComponent>
        ))}
      {accountDetails && (
        <div className="add-field-button flex justify-start w-full">
          <Button
            onClick={() => {
              setContactPersonsArray((prev: any) => [
                ...prev,
                {
                  id: null,
                  prefix: null,
                  fullName: null,
                  relationshipId: null,
                  email: null,
                  mobileNumber: null,
                },
              ]);
            }}
            className=" border-0 text-blue-500 bg-transparent mb-2 shadow-none p-0 text-base"
            icon={<PlusSquareOutlined />}
          >
            Add a new contact person
          </Button>
        </div>
      )}
      <CustomModal
        isVisible={openDeleteModal}
        centered
        width={450}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        className="delete-modal"
      >
        <DeleteModal
          onCancel={() => setOpenDeleteModal(false)}
          onConfirm={deleteContactPerson}
          text="Delete contact person!"
          subText={`Are you sure you want to delete this contact person?`}
        />
      </CustomModal>
    </div>
  );
};

export default EditContactPersons;
